/**
 * Berlin's seven-day forecast: Open-Meteo's weather code reduced to four
 * glyphs the strip can draw at 13px, and the response parsed defensively.
 *
 * No imports, the same rule `today.ts` and `perKind.ts` live by: `queries.ts`
 * fetches Open-Meteo and hands the JSON straight here, and nothing else in the
 * app needs to know that shape.
 */

export type WeatherIcon = 'sun' | 'part' | 'cloud' | 'rain'

export interface DayForecast {
  date: string
  icon: WeatherIcon
  /** Whole degrees C. Open-Meteo's own value has a decimal; the strip has
   *  room for a number, not a number and a fraction. */
  max: number
}

/**
 * One of Open-Meteo's forty WMO codes, reduced to the brief's four glyphs.
 *
 * Clear (0) is sun; mainly clear and partly cloudy (1, 2) are part; overcast
 * and both fog codes (3, 45, 48) are cloud. Everything else — every drizzle,
 * rain, snow, shower and thunderstorm code — is rain: the strip is "does this
 * day want a coat", not a weather station, and a snow code read as "cloud"
 * would be the one day the glyph actively misleads.
 */
export function iconFor(code: number): WeatherIcon {
  if (code === 0) return 'sun'
  if (code === 1 || code === 2) return 'part'
  if (code === 3 || code === 45 || code === 48) return 'cloud'
  return 'rain'
}

/**
 * Open-Meteo's `daily` block, read defensively.
 *
 * A malformed or empty response is seven missing cells, never a throw:
 * `queries.ts`'s `useWeather` already treats a failed *fetch* as a cell with
 * no weather (the brief's own rule), and a response that came back 200 but
 * empty or reshaped should fail the same quiet way rather than crashing the
 * strip a `catch` never sees, because a rejected promise and a malformed body
 * are the same fact from the day strip's side — there is no forecast for that
 * day — and the two disagreeing about it would be one more of CLAUDE.md's
 * "two things doing one job" bugs, with a network error the one that behaves
 * and a parse mismatch the one that does not.
 */
export function parseForecast(json: unknown): DayForecast[] {
  const daily = (json as { daily?: Record<string, unknown> } | null | undefined)?.daily
  const time = daily?.time
  const codes = daily?.weathercode
  const maxes = daily?.temperature_2m_max
  if (!Array.isArray(time) || !Array.isArray(codes) || !Array.isArray(maxes)) return []

  const out: DayForecast[] = []
  for (let i = 0; i < time.length; i++) {
    const max = maxes[i]
    if (typeof time[i] !== 'string' || typeof max !== 'number' || !Number.isFinite(max)) continue
    out.push({ date: time[i], icon: iconFor(Number(codes[i])), max: Math.round(max) })
  }
  return out
}
